// Types + helpers for the student-side AI Essay Review feature.
// Students NEVER receive AI rubric/suggestion fields — only essays, versions
// (drafts), and tutor comments (which are student-visible).

export interface EssayVersionSummary {
    id: number;
    versionNumber: number;
    label: string;
    wordCount: number;
    isLocked: boolean;            // locked once the draft has any review (analysis or comment)
    submittedAt: string | null;   // set when the student submits this draft for review
    hasPublishedComments: boolean;
    reviewFinished: boolean;      // tutor finished the review (comments shared, none pending)
    commentCount: number;         // published (student-visible) comments only
    createdAt: string;
    modifiedAt: string;
}

export interface EssaySummary {
    id: number;
    title: string;
    prompt: string;
    essayType: 'personal' | 'supplement';
    collegeId: string | null;
    collegeName: string | null;
    wordLimit: number | null;
    createdAt: string;
    modifiedAt: string;
    versions: EssayVersionSummary[];
}

export interface EssayVersionDetail {
    id: number;
    essayId: number;
    versionNumber: number;
    label: string;
    content: string;
    wordCount: number;
    isLocked: boolean;
    submittedAt: string | null;
    hasPublishedComments: boolean;
    reviewFinished: boolean;
    createdAt: string;
    modifiedAt: string;
}

// A draft's lifecycle from the student's point of view.
export type DraftState = 'editing' | 'in_review' | 'reviewed';

export function draftState(v: { submittedAt: string | null; isLocked: boolean; reviewFinished: boolean }): DraftState {
    // "Reviewed" means the tutor FINISHED the review (comments shared, none pending).
    // A tutor-only AI run locks a version (isLocked) without sharing anything, and a
    // reopened review (new pending comment) drops back to in_review — the student
    // can only branch a new draft from a finished review.
    if (v.reviewFinished) return 'reviewed';
    if (v.submittedAt) return 'in_review';
    return 'editing';
}

// The official Common App personal-statement prompts (2024–25 cycle, carried into
// 2025–26). Students pick exactly one; we store the full prompt text on the essay.
export const COMMON_APP_PROMPTS: string[] = [
    'Some students have a background, identity, interest, or talent so meaningful they believe their application would be incomplete without it. If this sounds like you, share your story.',
    'The lessons we take from obstacles can be fundamental to later success. Recount a time you faced a challenge, setback, or failure. How did it affect you, and what did you learn?',
    'Reflect on a time you questioned or challenged a belief or idea. What prompted your thinking? What was the outcome?',
    'Reflect on something someone has done for you that made you happy or thankful in a surprising way. How has this gratitude affected or motivated you?',
    'Discuss an accomplishment, event, or realization that sparked a period of personal growth and a new understanding of yourself or others.',
    'Describe a topic, idea, or concept you find so engaging it makes you lose all track of time. Why does it captivate you? What or who do you turn to to learn more?',
    "Share an essay on any topic of your choice — one you've already written, one that responds to a different prompt, or one of your own design.",
];

// Match a stored prompt back to its Common App number (1-based), or null if free choice.
export function promptNumber(prompt: string): number | null {
    const i = COMMON_APP_PROMPTS.indexOf((prompt || '').trim());
    return i === -1 ? null : i + 1;
}

export interface EssayComment {
    id: number;
    anchorStart: number;
    anchorEnd: number;
    anchorText: string;
    body: string;
    isGeneral: boolean;   // whole-essay comment (no anchor)
    isResolved: boolean;
    authorTutorId: number | null;
    authorName: string | null;
    createdAt: string;
    modifiedAt: string;
}

export function wordCount(text: string): number {
    return (text || '').trim() === '' ? 0 : text.trim().split(/\s+/).length;
}

export function formatRelative(iso: string | null, now: number = Date.now()): string {
    if (!iso) return '';
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return '';
    // Floor, not round — "3h ago" until a full 4 hours have passed (matches the
    // tutor app's relativeTime so both sides show the same age for the same edit).
    const seconds = Math.max(1, Math.floor((now - t) / 1000));
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}
